import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from '../../shared/_helpers';
import { executeLambda } from '../../../node/BackendData/LambdaExecutor';
import { Autocomplete, CircularProgress, Tooltip } from '@mui/material';
import TextField from '@mui/material/TextField';
import { registerDataGridRenderer } from '../../shared/datagrid/DataGridRegistry.jsx';
import BCP from '../../../common/section_bcp/DM_BCP';
import { Auth, CloudObject, User, ErrorFlow } from 'olympe';
import { canChangeAltVS } from '../../../common/section_bcp/BcpWorkflowAuthorizations';
import { updateProperty } from '../../shared/datagrid/DataGridRenderers.jsx';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { materialCacheManager } from './MaterialCacheManager';
import { callProxy, uniqueById } from './VendorRenderer.jsx';
import { removeZeros, padLeftWithZerosFinalDigits } from '../mass_import/utils.js';
import { getLogger } from '@olympeio/core';
const NO_OPTIONS_LABEL = 'No options';

/**
 * @param {AlternativeVendorDropdown.propTypes} props
 * @return {JSX.Element}
 */
export function AlternativeVendorDropdown(props) {
    const { multiSelect, selectedBCP } = props
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [currentVal, setCurrentVal] = useState(props.initialValue);
    const [textInput, setTextInput] = useState(props.initialValue);
    const debouncedTextInput = useDebounce(textInput, 1000);
    const [queriedText, setQueriedText] = useState('');
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const fetchAlternativeVendors = props.context.get('Fetch Alternative Vendors');
    const type = props.type;
    const bcpRequest = props.bcpRequest;
    const [excludedVendors, setExcludedVendors] = useState();
    const apiRef = "";

    //Formdatagird set to existing pass the context api not for vendordroodown on create popup
    if (props?.fromDataGrid) {
        apiRef = useGridApiContext();
    }

    // call to check the already updated value and show the value as current value for alternative
    useEffect(() => {
        setCurrentVal(props?.alternateVendorList?.[0]?.value)
    }, [props?.alternateVendorList])


    // call to check the already updated value and show the value as current value for mvlinked
    useEffect(() => {
        setCurrentVal(props?.mvLinkedList?.[0]?.value)
    }, [props?.mvLinkedList])

    // useEffect to handle queries executions to get the options for the autocomplete
    // initiated when textInput changes (with debouncing)
    useEffect(() => {
        // no parallel execution of queries
        if (isPerformingQuery) {
            return;
        }
        const searchInput = debouncedTextInput?.split('-')[0].trim();
        // avoid performing a query of with the same input as the last query
        // if (queriedText === searchInput && options.length > 0) {
        //     return;
        // }
        // avoid performing a query when the autocomplete is closed
        if (open === false) {
            return;
        }
        // start a query
        setIsPerformingQuery(true);

        // execute query
        setQueriedText(searchInput);
        setIsPerformingQuery(false);

        (async () => {
            const customErrorCode = 2311052908;
            try {
                const resultsString = await executeLambda(props.context, fetchAlternativeVendors, [props.bcpRequest, searchInput], customErrorCode);
                if (resultsString instanceof ErrorFlow) {
                    const msg = resultsString.getMessage();
                    props.context.set('Error information', { code: 2311492908, message: msg });
                    props.context.trigger('Open Error Popup');
                    console.error(`Alternative Vendor Dropdown error: ${msg}`);
                    return;
                }

                const results = JSON.parse(resultsString).results.map(value => {
                    return {
                        value: removeZeros(value.vendorSite) + "-" + value.vendorSiteName, label: removeZeros(value.vendorSite) + "-" + value.vendorSiteName
                    };
                });
                const excludedVendorsSet = await fetchExcludedVendors();
                console.log(":::excludedVendorsSet :::", excludedVendorsSet)
                if (type === 'AlternativeVendorDD') {
                    setExcludedVendors(excludedVendorsSet);
                }

                // Exclude the mainvendorid value from the excludedVendorsSet                
                else if (type === 'MVLinkedDD') {
                    setExcludedVendors(new Set());
                }
                setOptions(results.length > 0 ? results : [{ label: NO_OPTIONS_LABEL, value: '' }]);
                setQueriedText(searchInput);
            } catch (e) {
                getLogger("Alternate vendor dropdown").info("error happened", e)
            } finally {
                setIsPerformingQuery(false);
            }
        })();
    }, [bcpRequest, debouncedTextInput, open, queriedText, props.plantId, props.materailId]);
    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        console.log(inputValue, "inputValue from altvs")
        setTextInput(inputValue);
    };
    const handleChange = (event, newValue) => {
        if (!newValue) {
            //set a property to disable the vendor details on clear click
            props.setDisableVendor(false);

            setTextInput('');
            // if (props?.fromDataGrid) {
            //     props.onChange({ value: null }, apiRef);
            //     props.clearLine();
            // }
            //added this to update the value as null of alternative and mvlinked link if user clear the selected value
            props.onChange({ value: null }, apiRef);
            setCurrentVal(null);

        } else if (typeof newValue === 'string') {
            const paddedValue = newValue.padStart(10, '0');
            if (props?.fromDataGrid) {
                props.onChange({ value: paddedValue }, apiRef);
                props.clearLine();
            }
            setCurrentVal(paddedValue);
            // props.clearLine();
        } else {
            const paddedValue = { ...newValue, value: newValue?.value?.padStart(10, '0') };
            if (props?.fromDataGrid) {
                props.onChange(paddedValue, apiRef);
                props.clearLine();
            } else {
                props.onChange(paddedValue)
            }
            setCurrentVal(paddedValue.value);


        }
    };

    async function fetchExcludedVendors() {
        const pickSiteVendors = (materials) => uniqueById(materials.map((material) => {
            return { id: removeZeros(material.vendorSiteNumber), description: material.vendorSiteDescription };
        }));
        let plantArray = []
        let materialArray = []
        let plant = "";
        let material = "";
        let id;
        // Build id to check cache
        let body = {};
        if (multiSelect) {
            if (selectedBCP?.length > 1) {
                for (const [key, value] of Object?.entries(selectedBCP)) {
                    console.log("::BCP data::key, value, selectdBCPs", key, value);
                    let bcpRequestObject = CloudObject.get(value.tag) || value
                    plantArray.push(String(bcpRequestObject.get(BCP.plantIdProp)))
                    materialArray.push(String(bcpRequestObject.get(BCP.materialIdProp)))
                }

                for (let i = 0; i < plantArray?.length; i++) {
                    let currentPlant
                    if (i === plantArray?.length - 1) {
                        currentPlant = plantArray[i];
                        plant += "\"" + currentPlant.toString() + "\"";
                    } else {
                        currentPlant = plantArray[i];
                        plant += "\"" + currentPlant.toString() + "\",";
                    }
                }

                console.log(":::: plant string ::::", plant)

                console.log(":::: material Values ::::", material)
                body = {
                    offset: '0', limit: '100', plants: plantArray, materials: materialArray
                };
            } else {
                for (const [key, value] of Object?.entries(selectedBCP)) {
                    console.log("::BCP data::key, value, selectdBCPs", key, value);
                    let bcpRequestObject = CloudObject.get(value.tag) || value
                    plant = bcpRequestObject?.get(BCP.plantIdProp) || props.plantId;
                    material = bcpRequestObject?.get(BCP.materialIdProp) || props.materialId;
                    body = {
                        offset: '0', limit: '100', plants: [plant], materials: [material]
                    };
                }

            }
        }
        else {
            plant = props.bcpRequest?.get(BCP.plantIdProp) || props.plantId;
            material = props.bcpRequest?.get(BCP.materialIdProp) || props.materialId;
            body = {
                offset: '0', limit: '100', plants: [plant], materials: [material]
            };
        }

        console.log(":::::Alternative vendor plant, material ::::", plant, material, plant.replace(/\W/g, ''))
        id = `${plant}.${material};`;

        const materials = materialCacheManager.getFromCache(id);
        let siteVendors = [];
        if (materials !== undefined) {
            siteVendors = pickSiteVendors(materials);
        } else {
            // Build body to fetch vendors from plants & materials

            console.log(":::::body:::::", body);
            await callProxy(body).then((response) => {
                // API error
                if (response.statusCode === 500) {
                    console.error(`API Error: ${response.statusCode} - ${response.message}`);
                    return;
                }
                materialCacheManager.setInCache(id, response.materials);
                console.log("::::: Excluded vendore:::::", response);
                siteVendors = pickSiteVendors(response.materials);
            }).catch((error) => {
                console.log(error);
            });
        }
        return new Set(siteVendors.map((vendor) => vendor.id));
    }

    return (<Tooltip title={props.initialValue} placement={'top'} arrow>
        {<Autocomplete
            forcePopupIcon
            fullWidth
            clearOnBlur={false}
            freeSolo={false}
            size={'small'}
            open={open}
            options={options}
            value={currentVal}
            onChange={handleChange}
            filterOptions={(optionsArray) => {
                console.log(":::: optionsArray:::::", optionsArray)
                let newOptions = optionsArray?.filter(({ value, label }) => !excludedVendors?.has(value.slice(0, value?.indexOf('-'))));
                newOptions = newOptions?.length > 0 ? newOptions : [{ label: NO_OPTIONS_LABEL, value: '' }];
                return newOptions;
            }}
            disabled={props?.disabled}
            isOptionEqualToValue={(option, value) => {
                return value === undefined || value === '' || option.value === value;
            }}
            onOpen={() => {
                props.context.set('Actioned Object', props.bcpRequest);
                props.context.trigger('on indicator check');
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            loading={isPerformingQuery}
            blurOnSelect={true}
            getOptionDisabled={(option) => option.label === NO_OPTIONS_LABEL || option.label === 'Min. 3 characters'}
            renderInput={(params) => (<TextField
                {...params}
                variant={'outlined'}
                inputProps={{ ...params.inputProps, maxLength: 10 }}
                InputProps={{
                    ...params.InputProps, startAdornment: (<React.Fragment>
                        {isPerformingQuery || textInput !== debouncedTextInput ?
                            <CircularProgress color="inherit" size={13} /> : null}
                        {params.InputProps.startAdornment}
                    </React.Fragment>),
                }}
                onChange={handleInputChange}
            // onKeyDown={(e) => {
            //     // if (e.key === 'Enter') {
            //     //     e.stopPropagation();
            //     // }
            // }}
            />)}
        ></Autocomplete>}
    </Tooltip>);
}

AlternativeVendorDropdown.propTypes = {
    context: PropTypes.object.isRequired,
    bcpRequest: PropTypes.object.isRequired,
    onChange: PropTypes.func,
    initialValue: PropTypes.string,
    clearLine: PropTypes.func,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    fromDataGrid: PropTypes.bool,
    multiSelect: PropTypes.bool,
    selectedBCP: PropTypes.any
};

// To render the alternative and MV Linked Dropdown value 
const renderDropDown = (property, type) => {
    return (values, _options, object, $, column, refreshLine, setNewCellValue) => {
        const isAdmin = $.get('Is Admin');
        // actioned object is a bcp request
        const email = CloudObject.get(Auth.getCurrentUser()).get(User.loginProp);
        /** object is a BCP request
         * @type {BCP}
         * */
        const bcpRequest = object;
        const disabledEdition = !canChangeAltVS(isAdmin, email, bcpRequest, $);
        const api = "";
        if (props?.fromDataGrid) {
            api = apiRef
        }

        return (<AlternativeVendorDropdown
            context={$}
            bcpRequest={bcpRequest}
            onChange={(newVendor, api) => {
                const vendorVal = newVendor?.value?.slice(0, newVendor.value.indexOf('-')) || null
                updateProperty($, object, property, padLeftWithZerosFinalDigits(vendorVal, 10));
                setNewCellValue(padLeftWithZerosFinalDigits(vendorVal, 10), api);
            }}
            initialValue={values?.[0] || ''}
            clearLine={refreshLine}
            disabled={disabledEdition}
            type={type}
            fromDataGrid={props.fromDataGrid || true} //formdata use for to differentiate the form existing one to pass the correct data
            plantId={props.plantId}   //take th plantid and materail id from vendor dropdown for the create bcp 
            materialId={props.materialId}
            setAlternateVendorList={props.setAlternateVendorList}
            alternateVendorList={props.alternateVendorList}
            setMvLinkedList={props.setMvLinkedList}
            mvLinkedList={props.mvLinkedList}
        />);
    }
}

/**
 * WORKFLOW_CHIP
 * Render values BCP state
 */
// Render Alternative dropdown 
registerDataGridRenderer({
    name: 'AlternativeVendorDD', // renderForEdition: (value, object, property, $, options, column, refreshLine) => {
    render: renderDropDown(BCP.alternativeVendorCodeProp, 'AlternativeVendorDD')

});

// Render MV Linked dropdown 
registerDataGridRenderer({
    name: 'MVLinkedDD', // renderForEdition: (value, object, property, $, options, column, refreshLine) => {
    render: renderDropDown(BCP.mainVendorLinkedAltVendorProp, 'MVLinkedDD')
});